import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend } from 'chart.js';
import { Calendar } from 'lucide-react';
import {
  CIRCLE_COLORS,
  CIRCLE_LABELS,
  PM_ACTIONS,
  PM_INSIGHT_CHIPS,
  PM_INSIGHTS,
  PM_RAW,
  PM_RECOVERABLE_BANNER,
  PM_SPANS,
} from '../../data/pmData';
import {
  PM_FEB_ACTIONS,
  PM_FEB_HEADER_CHIPS,
  PM_FEB_INSIGHTS,
  PM_FEB_KPI_DEFAULTS,
  PM_FEB_RECOVERABLE_BANNER,
  PM_FEB_REFERENCE,
  PM_INTER_NB_FEB,
  PM_INTRA_NB_FEB,
  PM_RAW_FEB,
  PM_SPANS_FEB,
} from '../../data/pmFebData';
import {
  chartBaseOptions,
  EmptyState,
  ChipStrip,
  FilterSelect,
  formatLakhs,
  formatRupees,
  InsightCard,
  KpiCard,
  palette,
  PriorityBadge,
  SearchField,
  SectionTitle,
  SortButton,
  InsightBanner,
} from '../dashboardPrimitives';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const INTER_NB = {
  Kangra: 269.9,
  Mandi: 320.3,
  Shimla: 279.1,
  Srinagar: 515.1,
  Jammu: 613.0,
  Agra: 153.3,
  Aligarh: 447.6,
  Bijnor: 33.0,
  Bareilly: 0,
  Etawah: 184.7,
  Hapur: 247.0,
  Moradabad: 13.5,
  Meerut: 30.1,
  Muzaffarnagar: 92.2,
};

const INTRA_NB = {
  Kangra: 15.4,
  Mandi: 16.5,
  Shimla: 33.6,
  Srinagar: 11.0,
  Jammu: 91.2,
  Agra: 57.9,
  Aligarh: 7.7,
  Bijnor: 26.5,
  Bareilly: 0,
  Etawah: 5.8,
  Hapur: 4.0,
  Moradabad: 0,
  Meerut: 0,
  Muzaffarnagar: 100.8,
};

const MP_RATES = PM_RAW.reduce((accumulator, row) => {
  accumulator[row.mp] = {
    pmRate: row.pm / row.total_km,
    x2Rate: row.x2_km > 0 ? row.x2_rs / row.x2_km : 0,
  };
  return accumulator;
}, {});

const SPAN_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Risk States' },
  { value: 'X2RISK', label: '2X Risk only' },
  { value: 'ZERO', label: 'Zero billable' },
  { value: 'CRITICAL', label: 'Critical < 50%' },
  { value: 'PARTIAL', label: 'Partial 50-89%' },
  { value: 'BILLABLE', label: 'Billable 90%+' },
];

const FEB_SPAN_STATUS_OPTIONS = [
  { value: 'ALL', label: 'All Status Types' },
  { value: 'X2DED', label: '2X Deduction' },
  { value: 'X2RISK', label: '2X Risk' },
  { value: 'CRITICAL', label: 'Critical' },
];

function complianceColor(percent) {
  if (percent >= 98) return palette.success;
  if (percent >= 93) return palette.accent;
  if (percent >= 88) return palette.slate;
  if (percent >= 83) return palette.warning;
  return palette.danger;
}

function missClass(percent) {
  if (percent > 15) return 'text-red-500';
  if (percent > 8) return 'text-amber-600';
  return 'text-emerald-600';
}

function detailSortValue(row, key) {
  switch (key) {
    case 'circle':
    case 'mp':
      return row[key];
    default:
      return row[key] ?? 0;
  }
}

function spanAtRisk(span) {
  const rate = MP_RATES[span.mp];
  if (!rate) return 0;
  const baseValue = span.km * rate.pmRate * (1 - Math.min(span.dec, 100) / 100);
  const x2Value = span.x2 ? span.km * rate.x2Rate : 0;
  return Math.round(baseValue + x2Value);
}

function formatRisk(value) {
  if (!value) return '₹0';
  if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
  return formatRupees(value);
}

function spanStatus(dec, x2) {
  if (x2) return { label: '2X Risk', className: 'status-badge status-badge-red' };
  if (dec === 0) return { label: 'Zero', className: 'status-badge status-badge-red' };
  if (dec < 50) return { label: 'Critical', className: 'status-badge status-badge-red' };
  if (dec < 90) return { label: 'Partial', className: 'status-badge status-badge-amber' };
  return { label: 'Billable', className: 'status-badge status-badge-green' };
}

function scoreBarColor(value) {
  if (value === null || value === undefined) return palette.slate;
  if (value === 0) return palette.navyStrong;
  if (value < 50) return palette.danger;
  if (value < 90) return palette.warning;
  return palette.success;
}

function ComplianceBar({ value }) {
  const color = scoreBarColor(value);
  return (
    <div className="flex min-w-[118px] items-center gap-2">
      <span className="mono-cell text-[11px] font-semibold" style={{ color }}>
        {value.toFixed(1)}%
      </span>
      <div className="h-1.5 flex-1 rounded-full bg-slate-100">
        <div className="h-full rounded-full" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

const pmFebAverageLinePlugin = {
  id: 'pmFebAverageLine',
  afterDatasetsDraw(chart, _args, pluginOptions) {
    if (!pluginOptions || typeof pluginOptions.value !== 'number') return;

    const { ctx, chartArea, scales } = chart;
    const xScale = scales?.x;
    if (!ctx || !chartArea || !xScale) return;

    const pixel = xScale.getPixelForValue(pluginOptions.value);
    const label = pluginOptions.label ?? `${pluginOptions.value}%`;

    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1;
    ctx.strokeStyle = palette.slateDeep;
    ctx.moveTo(pixel, chartArea.top);
    ctx.lineTo(pixel, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = palette.slateDeep;
    ctx.font = '600 10px Inter';
    ctx.textAlign = 'left';
    ctx.fillText(label, Math.min(pixel + 8, chartArea.right - 96), chartArea.top + 14);
    ctx.restore();
  },
};

function PendingBillingChartState({ subtitle = PM_FEB_REFERENCE.pendingChartSubtitle }) {
  return (
    <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-app-border bg-slate-50/70 px-6 text-center">
      <Calendar className="h-5 w-5 text-slate-300" />
      <div className="mt-3 text-[14px] font-semibold text-slate-500">Data not available</div>
      <div className="mt-1 text-[12px] leading-5 text-slate-400">{subtitle}</div>
    </div>
  );
}

function PendingBillingHeader({ label, sortKey, sortState, onSort }) {
  return (
    <div className="flex flex-col items-start gap-0.5">
      <SortButton label={label} sortKey={sortKey} sortState={sortState} onSort={onSort} />
      <span className="text-[9px] font-normal normal-case leading-tight tracking-normal text-slate-400">
        {PM_FEB_REFERENCE.pendingBillingLabel}
      </span>
    </div>
  );
}

function complianceChipTone(value) {
  const normalized = String(value ?? '').trim().toLowerCase();
  if (normalized === 'pass') return { backgroundColor: '#DCFCE7', color: '#16A34A' };
  if (normalized === 'fail') return { backgroundColor: '#FEE2E2', color: '#DC2626' };
  return { backgroundColor: '#F1F5F9', color: '#64748B' };
}

function ComplianceChip({ value }) {
  const tone = complianceChipTone(value);
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={tone}
    >
      {value}
    </span>
  );
}

function febSpanStatus(span) {
  if (span.ded) return { label: '2X Deduction', className: 'status-badge status-badge-red' };
  if (span.x2) return { label: '2X Risk', className: 'status-badge status-badge-red' };
  return { label: 'Critical', className: 'status-badge status-badge-amber' };
}

function PMPenaltyFeb({
  activeCircle,
  detailSearch,
  detailSort,
  setActiveCircle,
  setDetailSearch,
  setSpanMpFilter,
  setSpanSearch,
  setSpanStatusFilter,
  spanMpFilter,
  spanSearch,
  spanStatusFilter,
  updateDetailSort,
}) {
  const filteredRows = useMemo(() => {
    return activeCircle === 'ALL' ? PM_RAW_FEB : PM_RAW_FEB.filter((row) => row.circle === activeCircle);
  }, [activeCircle]);

  const spansInScope = useMemo(() => {
    return activeCircle === 'ALL' ? PM_SPANS_FEB : PM_SPANS_FEB.filter((span) => span.circle === activeCircle);
  }, [activeCircle]);

  const spanMpOptions = useMemo(() => {
    const values = [...new Set(spansInScope.map((span) => span.mp))].sort((left, right) => left.localeCompare(right));
    return [{ value: 'ALL', label: 'All MPs' }, ...values.map((value) => ({ value, label: value }))];
  }, [spansInScope]);

  useEffect(() => {
    if (spanMpFilter !== 'ALL' && !spanMpOptions.some((option) => option.value === spanMpFilter)) {
      setSpanMpFilter('ALL');
    }
  }, [setSpanMpFilter, spanMpFilter, spanMpOptions]);

  useEffect(() => {
    if (!FEB_SPAN_STATUS_OPTIONS.some((option) => option.value === spanStatusFilter)) {
      setSpanStatusFilter('ALL');
    }
  }, [setSpanStatusFilter, spanStatusFilter]);

  const totals = useMemo(() => {
    const totalNbKm = filteredRows.reduce((sum, row) => sum + row.nb_km, 0);
    const totalScopeKm = filteredRows.reduce((sum, row) => sum + row.total_km, 0);
    const avgCompliance = filteredRows.reduce((sum, row) => sum + row.bill_pct, 0) / Math.max(filteredRows.length, 1);
    const repeatFailures = filteredRows.filter((row) => row.has_x2).length;
    const x2RiskKm = filteredRows.reduce((sum, row) => sum + row.x2_km, 0);
    const x2RiskSpans = spansInScope.filter((span) => span.x2).length;
    const x2DeductionSpans = spansInScope.filter((span) => span.ded).length;
    const topExposure = [...filteredRows].sort((left, right) => right.nb_km - left.nb_km)[0] ?? null;
    const bestCompliance = [...filteredRows].sort((left, right) => right.bill_pct - left.bill_pct)[0] ?? null;
    const worstBilling = [...filteredRows].sort((left, right) => left.bill_pct - right.bill_pct)[0] ?? null;
    const jammu = PM_RAW_FEB.find((row) => row.mp === 'Jammu') ?? null;

    return {
      avgCompliance,
      bestCompliance,
      jammu,
      repeatFailures,
      topExposure,
      totalNbKm,
      totalScopeKm,
      worstBilling,
      x2DeductionSpans,
      x2RiskKm,
      x2RiskSpans,
    };
  }, [filteredRows, spansInScope]);

  const complianceRows = useMemo(
    () => [...filteredRows].sort((left, right) => left.bill_pct - right.bill_pct),
    [filteredRows],
  );

  const nbRows = useMemo(
    () => [...filteredRows].sort((left, right) => right.nb_km - left.nb_km),
    [filteredRows],
  );

  const x2Rows = useMemo(
    () => [...filteredRows].sort((left, right) => right.x2_km - left.x2_km),
    [filteredRows],
  );

  const detailRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((left, right) => {
      const leftValue = detailSortValue(left, detailSort.key);
      const rightValue = detailSortValue(right, detailSort.key);
      if (typeof leftValue === 'string') {
        return detailSort.dir === 'asc'
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }
      return detailSort.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    });

    if (!detailSearch.trim()) return rows;

    const query = detailSearch.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [detailSearch, detailSort, filteredRows]);

  const spanRows = useMemo(() => {
    let rows = spansInScope;

    if (spanMpFilter !== 'ALL') {
      rows = rows.filter((span) => span.mp === spanMpFilter);
    }

    if (spanStatusFilter !== 'ALL') {
      rows = rows.filter((span) => {
        if (spanStatusFilter === 'X2DED') return span.ded;
        if (spanStatusFilter === 'X2RISK') return span.x2;
        if (spanStatusFilter === 'CRITICAL') return !span.ded && !span.x2;
        return true;
      });
    }

    const sortedRows = [...rows].sort((left, right) => right.km - left.km);

    if (!spanSearch.trim()) return sortedRows;

    const query = spanSearch.toLowerCase();
    return sortedRows.filter((span) => (
      span.span.toLowerCase().includes(query)
      || span.mp.toLowerCase().includes(query)
      || span.circle.toLowerCase().includes(query)
    ));
  }, [spanMpFilter, spanSearch, spanStatusFilter, spansInScope]);

  const exposureRow = activeCircle === 'ALL' ? totals.jammu : totals.topExposure;
  const exposureLabel = activeCircle === 'ALL' ? 'Jammu Exposure' : 'Top MP Exposure';
  const exposureValue = '-';
  const exposureSub = exposureRow
    ? `${activeCircle === 'ALL' ? 'Largest single-MP deduction' : 'Highest single-MP deduction in active scope'} (${exposureRow.bill_pct.toFixed(1)}% billing)`
    : 'Pending billing finalisation';

  if (!filteredRows.length) {
    return (
      <div className="space-y-6">
        <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label })) }]} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label })) }]} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.totalDeduction.label}
          value="-"
          sub={`Data not available · ${filteredRows.length} MP${filteredRows.length !== 1 ? 's' : ''} in scope`}
          dot={palette.danger}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label={exposureLabel}
          value={exposureValue}
          sub={exposureSub}
          dot={palette.warning}
          valueColor={palette.warning}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.repeatPenalty.label}
          value="-"
          sub={`Data not available · ${totals.x2RiskSpans} spans at 2X risk`}
          dot={palette.danger}
          valueColor={palette.slateDeep}
          pill={`${totals.repeatFailures} MPs affected`}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.nonBillableKm.label}
          value={`${totals.totalNbKm.toFixed(0)} KM`}
          sub={`${((totals.totalNbKm / Math.max(totals.totalScopeKm, 1)) * 100).toFixed(1)}% of total scope · Intercity + Intracity combined`}
          dot={palette.accent}
          valueColor={palette.navy}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.bestCompliance.label}
          value={totals.bestCompliance ? totals.bestCompliance.mp : '-'}
          sub={totals.bestCompliance ? `${totals.bestCompliance.bill_pct.toFixed(1)}% billable · ${totals.bestCompliance.total_km.toLocaleString('en-IN', { maximumFractionDigits: 0 })} KM scope` : '-'}
          dot={palette.success}
          valueColor={palette.success}
          pill={totals.bestCompliance ? totals.bestCompliance.circle : null}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.highestRate.label}
          value="-"
          sub={totals.worstBilling ? `Data not available · ${totals.worstBilling.mp} worst at ${totals.worstBilling.bill_pct.toFixed(1)}% billing` : PM_FEB_KPI_DEFAULTS.highestRate.sub}
          dot={palette.warning}
          valueColor={palette.slateDeep}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.averageCompliance.label}
          value={`${totals.avgCompliance.toFixed(1)}%`}
          sub={activeCircle === 'ALL' ? "↓ from 91.3% in Dec'25 · Billable KMs as % of scope" : PM_FEB_KPI_DEFAULTS.averageCompliance.sub}
          dot={palette.accent}
          valueColor={palette.navy}
        />
        <KpiCard
          label={PM_FEB_KPI_DEFAULTS.repeatFailureMps.label}
          value={`${totals.repeatFailures} MPs`}
          sub={totals.repeatFailures === filteredRows.length ? 'All MPs - consecutive non-compliance triggers surcharge' : `${totals.repeatFailures} MPs - consecutive non-compliance triggers surcharge`}
          dot={palette.danger}
          valueColor={palette.danger}
          pill="Immediate action needed"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="card-surface p-6 lg:col-span-2">
          <SectionTitle dot={palette.accent}>Deduction by Circle</SectionTitle>
          <p className="dashboard-note">Billing is pending for Feb'26, so financial circle breakouts are unavailable.</p>
          <div style={{ height: 264 }}>
            <PendingBillingChartState />
          </div>
        </div>

        <div className="card-surface p-6 lg:col-span-3">
          <SectionTitle dot={palette.navy}>Deduction by MP</SectionTitle>
          <p className="dashboard-note">Financial deductions are awaiting billing finalisation for the active period.</p>
          <div style={{ height: 264 }}>
            <PendingBillingChartState />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>PM Compliance Rate by MP (%)</SectionTitle>
          <p className="dashboard-note">Sorted descending. The dashed reference line marks the 85.3% zone average.</p>
          <div style={{ height: 320 }}>
            <Bar
              plugins={[pmFebAverageLinePlugin]}
              data={{
                labels: complianceRows.map((row) => row.mp),
                datasets: [
                  {
                    data: complianceRows.map((row) => row.bill_pct),
                    backgroundColor: complianceRows.map((row) => complianceColor(row.bill_pct)),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: false },
                  pmFebAverageLine: {
                    value: PM_FEB_REFERENCE.zoneAverage,
                    label: `Zone avg ${PM_FEB_REFERENCE.zoneAverage}%`,
                  },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: {
                      label: (context) => ` ${context.raw.toFixed(1)}%`,
                    },
                  },
                },
                scales: {
                  x: {
                    min: 0,
                    max: 100,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `${value}%`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                  y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Non-Billable KM Split - Intercity vs Intracity</SectionTitle>
          <p className="dashboard-note">Intercity and intracity misses remain fully available as KM-based operational data.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: nbRows.map((row) => row.mp),
                datasets: [
                  {
                    label: 'Intercity NB KM',
                    data: nbRows.map((row) => PM_INTER_NB_FEB[row.mp] || 0),
                    backgroundColor: palette.navy,
                    borderRadius: 4,
                  },
                  {
                    label: 'Intracity NB KM',
                    data: nbRows.map((row) => PM_INTRA_NB_FEB[row.mp] || 0),
                    backgroundColor: palette.accent,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: {
                      label: (context) => ` ${context.raw.toFixed(1)} KM`,
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    stacked: true,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `${value} KM`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>Deduction per 1,000 Span-KM</SectionTitle>
          <p className="dashboard-note">Deduction intensity will populate once billing rates are available for Feb'26.</p>
          <div style={{ height: 280 }}>
            <PendingBillingChartState />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>2X Consecutive Failure Tracker</SectionTitle>
          <p className="dashboard-note">KM at 2X risk by MP, sorted by exposure from highest to lowest.</p>
          <div style={{ height: 280 }}>
            <Bar
              data={{
                labels: x2Rows.map((row) => row.mp),
                datasets: [
                  {
                    label: '2X Risk KM',
                    data: x2Rows.map((row) => row.x2_km),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  ...chartBaseOptions.plugins,
                  legend: { display: false },
                  tooltip: {
                    ...chartBaseOptions.plugins.tooltip,
                    callbacks: {
                      label: (context) => ` ${context.raw.toFixed(1)} KM`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `${value} KM`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.navy}>MP-Level PM Performance Detail</SectionTitle>
            <p className="dashboard-note mb-0">Financial columns remain pending; compliance and KM-based fields are fully available.</p>
          </div>
          <SearchField value={detailSearch} onChange={setDetailSearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        {detailRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="Circle" sortKey="circle" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="MP" sortKey="mp" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><PendingBillingHeader label="PM Scope" sortKey="pm" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><PendingBillingHeader label="Billable" sortKey="bill" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><PendingBillingHeader label="Deduction" sortKey="ded" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><PendingBillingHeader label="2X Penalty" sortKey="x2_rs" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Compliance %" sortKey="bill_pct" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="NB KM" sortKey="nb_km" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><PendingBillingHeader label="Ded/1000 KM" sortKey="pen_per_km" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Intercity Miss" sortKey="inter_miss" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Intracity Miss" sortKey="intra_miss" sortState={detailSort} onSort={updateDetailSort} /></th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row) => (
                  <tr key={row.mp}>
                    <td>
                      <span
                        className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                        style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                      >
                        {row.circle}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-700">{row.mp}</span>
                        {row.has_x2 ? <span className="status-badge status-badge-red">2X</span> : null}
                      </div>
                    </td>
                    <td className="mono-cell text-slate-400">-</td>
                    <td className="mono-cell text-slate-400">-</td>
                    <td className="mono-cell text-slate-400">-</td>
                    <td className="mono-cell text-slate-400">-</td>
                    <td><ComplianceBar value={row.bill_pct} /></td>
                    <td className="mono-cell">{row.nb_km.toFixed(1)}</td>
                    <td className="mono-cell text-slate-400">-</td>
                    <td className={`mono-cell font-semibold ${missClass(row.inter_miss)}`}>{row.inter_miss.toFixed(1)}%</td>
                    <td className={`mono-cell font-semibold ${missClass(row.intra_miss)}`}>{row.intra_miss.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      <div className="card-surface p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="flex-1">
            <SectionTitle dot={palette.danger}>Span-Level PM Compliance Drilldown</SectionTitle>
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-[12px] leading-6 text-slate-500">
              {PM_FEB_REFERENCE.spanNotice}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
              <span>{`${spansInScope.length} spans in active scope`}</span>
              <span className="summary-chip bg-red-50 text-red-600">{`${totals.x2RiskSpans} with 2X risk`}</span>
              <span className="summary-chip bg-amber-50 text-amber-700">{`${totals.x2DeductionSpans} with 2X deduction`}</span>
              <span className="summary-chip bg-slate-100 text-slate-600">{`${spanRows.length} spans shown`}</span>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:min-w-[520px] sm:flex-row">
            <SearchField value={spanSearch} onChange={setSpanSearch} placeholder="Search spans..." className="flex-1 sm:min-w-[220px]" />
            <FilterSelect label="MP" value={spanMpFilter} onChange={setSpanMpFilter} options={spanMpOptions} className="flex-1" />
            <FilterSelect label="Risk Filter" value={spanStatusFilter} onChange={setSpanStatusFilter} options={FEB_SPAN_STATUS_OPTIONS} className="flex-1" />
          </div>
        </div>

        {spanRows.length ? (
          <div className="table-scroll-shell-bordered">
            <table className="evidence-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>#</th>
                  <th>Span</th>
                  <th>MP</th>
                  <th>Circle</th>
                  <th>KM</th>
                  <th>Amount at Risk</th>
                  <th>Jan'26 Compliance</th>
                  <th>Feb'26 Compliance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {spanRows.map((span, index) => {
                  const status = febSpanStatus(span);
                  return (
                    <tr key={`${span.mp}-${span.span}`} className={span.ded || span.x2 ? 'bg-red-50/70' : ''}>
                      <td className="mono-cell text-slate-400">{index + 1}</td>
                      <td className="max-w-[280px] whitespace-normal pr-6 font-medium text-slate-700">
                        <div className="flex items-start gap-2">
                          <span>{span.span}</span>
                          {span.ded || span.x2 ? <span className="status-badge status-badge-red">2X</span> : null}
                        </div>
                      </td>
                      <td>{span.mp}</td>
                      <td>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${CIRCLE_COLORS[span.circle]}16`, color: CIRCLE_COLORS[span.circle] }}
                        >
                          {span.circle}
                        </span>
                      </td>
                      <td className="mono-cell">{span.km.toFixed(1)}</td>
                      <td className="mono-cell text-slate-400">-</td>
                      <td><ComplianceChip value={span.nov} /></td>
                      <td><ComplianceChip value={span.dec} /></td>
                      <td><span className={status.className}>{status.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="min-h-[220px]" />
        )}
      </div>

      <InsightBanner chips={PM_FEB_HEADER_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {PM_FEB_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          />
        ))}
      </div>

      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{PM_FEB_RECOVERABLE_BANNER}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>MPs Impacted</th>
                <th>Owner</th>
                <th>Expected Impact</th>
              </tr>
            </thead>
            <tbody>
              {PM_FEB_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="font-semibold text-slate-700">{row.action}</div>
                    {row.detail ? <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.detail}</div> : null}
                  </td>
                  <td className="text-slate-600">{row.impacted}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function PMPenalty({ selectedMonth = "Dec'25" }) {
  const [activeCircle, setActiveCircle] = useState('ALL');
  const [detailSort, setDetailSort] = useState({ key: 'ded', dir: 'desc' });
  const [detailSearch, setDetailSearch] = useState('');
  const [spanMpFilter, setSpanMpFilter] = useState('ALL');
  const [spanStatusFilter, setSpanStatusFilter] = useState('ALL');
  const [spanSearch, setSpanSearch] = useState('');

  const filteredRows = useMemo(() => {
    return activeCircle === 'ALL' ? PM_RAW : PM_RAW.filter((row) => row.circle === activeCircle);
  }, [activeCircle]);

  const spansInScope = useMemo(() => {
    return activeCircle === 'ALL' ? PM_SPANS : PM_SPANS.filter((span) => span.circle === activeCircle);
  }, [activeCircle]);

  const spanMpOptions = useMemo(() => {
    const values = [...new Set(spansInScope.map((span) => span.mp))].sort((left, right) => left.localeCompare(right));
    return [{ value: 'ALL', label: 'All MPs' }, ...values.map((value) => ({ value, label: value }))];
  }, [spansInScope]);

  useEffect(() => {
    if (spanMpFilter !== 'ALL' && !spanMpOptions.some((option) => option.value === spanMpFilter)) {
      setSpanMpFilter('ALL');
    }
  }, [spanMpFilter, spanMpOptions]);

  useEffect(() => {
    const validOptions = selectedMonth === "Feb'26" ? FEB_SPAN_STATUS_OPTIONS : SPAN_STATUS_OPTIONS;
    if (!validOptions.some((option) => option.value === spanStatusFilter)) {
      setSpanStatusFilter('ALL');
    }
  }, [selectedMonth, spanStatusFilter]);

  const totals = useMemo(() => {
    const totalDeduction = filteredRows.reduce((sum, row) => sum + row.ded, 0);
    const total2x = filteredRows.reduce((sum, row) => sum + row.x2_rs, 0);
    const totalNbKm = filteredRows.reduce((sum, row) => sum + row.nb_km, 0);
    const avgCompliance = filteredRows.reduce((sum, row) => sum + row.bill_pct, 0) / Math.max(filteredRows.length, 1);
    const repeatFailures = filteredRows.filter((row) => row.has_x2).length;
    const topDeduction = [...filteredRows].sort((left, right) => right.ded - left.ded)[0] ?? null;
    const highestRate = [...filteredRows].sort((left, right) => right.pen_per_km - left.pen_per_km)[0] ?? null;
    const perfectRow = filteredRows.find((row) => row.bill_pct === 100)
      ?? [...filteredRows].sort((left, right) => right.bill_pct - left.bill_pct)[0]
      ?? null;

    return {
      totalDeduction,
      total2x,
      totalNbKm,
      avgCompliance,
      repeatFailures,
      topDeduction,
      highestRate,
      perfectRow,
    };
  }, [filteredRows]);

  const circleDeduction = useMemo(() => {
    return ['HP', 'KS', 'JA', 'UW'].map((circle) =>
      filteredRows.filter((row) => row.circle === circle).reduce((sum, row) => sum + row.ded, 0),
    );
  }, [filteredRows]);

  const sortedByDeduction = useMemo(() => [...filteredRows].sort((left, right) => right.ded - left.ded), [filteredRows]);
  const sortedByCompliance = useMemo(() => [...filteredRows].sort((left, right) => left.bill_pct - right.bill_pct), [filteredRows]);
  const sortedByNbKm = useMemo(() => [...filteredRows].sort((left, right) => right.nb_km - left.nb_km), [filteredRows]);
  const sortedByRate = useMemo(() => [...filteredRows].sort((left, right) => right.pen_per_km - left.pen_per_km), [filteredRows]);
  const x2Rows = useMemo(() => [...filteredRows].filter((row) => row.x2_rs > 0).sort((left, right) => right.x2_rs - left.x2_rs), [filteredRows]);

  const detailRows = useMemo(() => {
    const rows = [...filteredRows];
    rows.sort((left, right) => {
      const leftValue = detailSortValue(left, detailSort.key);
      const rightValue = detailSortValue(right, detailSort.key);
      if (typeof leftValue === 'string') {
        return detailSort.dir === 'asc'
          ? leftValue.localeCompare(rightValue)
          : rightValue.localeCompare(leftValue);
      }
      return detailSort.dir === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    });
    if (!detailSearch.trim()) return rows;
    const query = detailSearch.toLowerCase();
    return rows.filter((row) => row.mp.toLowerCase().includes(query) || row.circle.toLowerCase().includes(query));
  }, [detailSearch, detailSort, filteredRows]);

  const spanRows = useMemo(() => {
    let rows = spansInScope;
    if (spanMpFilter !== 'ALL') {
      rows = rows.filter((span) => span.mp === spanMpFilter);
    }
    if (spanStatusFilter !== 'ALL') {
      rows = rows.filter((span) => {
        if (spanStatusFilter === 'X2RISK') return span.x2;
        if (spanStatusFilter === 'ZERO') return span.dec === 0;
        if (spanStatusFilter === 'CRITICAL') return span.dec > 0 && span.dec < 50;
        if (spanStatusFilter === 'PARTIAL') return span.dec >= 50 && span.dec < 90;
        if (spanStatusFilter === 'BILLABLE') return span.dec >= 90;
        return true;
      });
    }

    const scopedRows = rows
      .map((span) => ({ ...span, atRisk: spanAtRisk(span) }))
      .sort((left, right) => right.atRisk - left.atRisk || left.dec - right.dec);

    if (!spanSearch.trim()) return scopedRows;
    const query = spanSearch.toLowerCase();
    return scopedRows.filter((span) => (
      span.span.toLowerCase().includes(query)
      || span.mp.toLowerCase().includes(query)
      || span.circle.toLowerCase().includes(query)
    ));
  }, [spanMpFilter, spanSearch, spanStatusFilter, spansInScope]);

  const spanTotalRisk = useMemo(() => spanRows.reduce((sum, span) => sum + span.atRisk, 0), [spanRows]);
  const totalSpanRiskCount = useMemo(() => spansInScope.filter((span) => span.x2).length, [spansInScope]);

  const updateDetailSort = (key) => {
    setDetailSort((current) => ({
      key,
      dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  if (selectedMonth === "Feb'26") {
    return (
      <PMPenaltyFeb
        activeCircle={activeCircle}
        detailSearch={detailSearch}
        detailSort={detailSort}
        setActiveCircle={setActiveCircle}
        setDetailSearch={setDetailSearch}
        setSpanMpFilter={setSpanMpFilter}
        setSpanSearch={setSpanSearch}
        setSpanStatusFilter={setSpanStatusFilter}
        spanMpFilter={spanMpFilter}
        spanSearch={spanSearch}
        spanStatusFilter={spanStatusFilter}
        updateDetailSort={updateDetailSort}
      />
    );
  }

  if (!filteredRows.length) {
    return (
      <div className="space-y-6">
        <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label })) }]} />
        <EmptyState />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ChipStrip groups={[{ label: 'Circle', value: activeCircle, onChange: setActiveCircle, options: Object.entries(CIRCLE_LABELS).map(([value, label]) => ({ value, label })) }]} />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Total PM Deduction"
          value={formatLakhs(totals.totalDeduction)}
          sub="Total PM leakage across all MPs"
          dot={palette.danger}
          valueColor={palette.danger}
          pill={`${filteredRows.length} MPs in scope`}
        />
        <KpiCard
          label={activeCircle === 'ALL' ? 'Jammu Exposure' : 'Top MP Exposure'}
          value={totals.topDeduction ? formatLakhs(totals.topDeduction.ded) : '-'}
          sub={totals.topDeduction ? `${totals.topDeduction.mp} carries the highest deduction load` : 'No data in scope'}
          dot={palette.warning}
          valueColor={palette.warning}
          pill={totals.topDeduction ? `${totals.topDeduction.circle} Circle` : null}
        />
        <KpiCard
          label="2X Repeat Penalty"
          value={formatLakhs(totals.total2x)}
          sub={`${totals.repeatFailures} MPs currently in repeat-failure state`}
          dot={palette.danger}
          valueColor={palette.danger}
          pill={`${totalSpanRiskCount} spans with 2X risk`}
        />
        <KpiCard
          label="Non-Billable KM"
          value={`${totals.totalNbKm.toFixed(0)} KM`}
          sub="Total non-billable movement against contracted scope"
          dot={palette.accent}
          valueColor={palette.navy}
          pill="Intercity + intracity"
        />
        <KpiCard
          label="Best Compliance MP"
          value={totals.perfectRow?.mp ?? '-'}
          sub={totals.perfectRow ? `${totals.perfectRow.bill_pct.toFixed(1)}% billable against ${totals.perfectRow.total_km.toLocaleString('en-IN')} KM` : 'No data in scope'}
          dot={palette.success}
          valueColor={palette.success}
          pill={totals.perfectRow ? totals.perfectRow.circle : null}
        />
        <KpiCard
          label="Highest Ded. Rate"
          value={totals.highestRate ? formatLakhs(totals.highestRate.pen_per_km * 1000) : '-'}
          sub={totals.highestRate ? `${totals.highestRate.mp} has the highest rupee loss per 1,000 span-KM` : 'No data in scope'}
          dot={palette.warning}
          valueColor={palette.warning}
          pill="₹ per 1,000 span-KM"
        />
        <KpiCard
          label="Avg Compliance"
          value={`${totals.avgCompliance.toFixed(1)}%`}
          sub="Billable KM percentage across the active scope"
          dot={palette.accent}
          valueColor={palette.navy}
          pill="Operational billing quality"
        />
        <KpiCard
          label="Repeat-Failure MPs"
          value={`${totals.repeatFailures} MPs`}
          sub="Consecutive misses triggering the 2X charge"
          dot={palette.danger}
          valueColor={palette.danger}
          pill={totals.repeatFailures ? 'Immediate close-out needed' : 'No repeat penalties'}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        <div className="card-surface p-6 lg:col-span-2">
          <SectionTitle dot={palette.accent}>Deduction by Circle</SectionTitle>
          <p className="dashboard-note">Circle contribution to PM penalty based on the active circle filter.</p>
          <div style={{ height: 264 }}>
            <Doughnut
              data={{
                labels: ['HP', 'KS', 'JA', 'UW'].map((circle) => `${circle} Circle`),
                datasets: [
                  {
                    data: circleDeduction,
                    backgroundColor: ['HP', 'KS', 'JA', 'UW'].map((circle) => CIRCLE_COLORS[circle]),
                    spacing: 2,
                    cutout: '68%',
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.raw || 0;
                        const pct = totals.totalDeduction > 0 ? ((value / totals.totalDeduction) * 100).toFixed(1) : '0.0';
                        return ` ${formatLakhs(value)} (${pct}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6 lg:col-span-3">
          <SectionTitle dot={palette.navy}>Deduction by MP</SectionTitle>
          <p className="dashboard-note">Sorted by impact. Non-billable loss and 2X surcharge remain separated.</p>
          <div style={{ height: 264 }}>
            <Bar
              data={{
                labels: sortedByDeduction.map((row) => row.mp),
                datasets: [
                  {
                    label: 'Non-Billable',
                    data: sortedByDeduction.map((row) => row.nb_rs),
                    backgroundColor: palette.accent,
                    borderRadius: 4,
                  },
                  {
                    label: '2X Deduction',
                    data: sortedByDeduction.map((row) => row.x2_rs),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${formatRupees(context.raw)}`,
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    stacked: true,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => (value >= 100000 ? `₹${(value / 100000).toFixed(1)}L` : `₹${value}`),
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>PM Compliance Rate by MP</SectionTitle>
          <p className="dashboard-note">Compliance is kept technical and threshold-driven rather than decorative.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: sortedByCompliance.map((row) => row.mp),
                datasets: [
                  {
                    data: sortedByCompliance.map((row) => row.bill_pct),
                    backgroundColor: sortedByCompliance.map((row) => complianceColor(row.bill_pct)),
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                indexAxis: 'y',
                scales: {
                  x: {
                    min: 0,
                    max: 100,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `${value}%`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                  y: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.accent}>Non-Billable KM Split</SectionTitle>
          <p className="dashboard-note">Intercity and intracity misses show where the lost billing is accumulating.</p>
          <div style={{ height: 320 }}>
            <Bar
              data={{
                labels: sortedByNbKm.map((row) => row.mp),
                datasets: [
                  {
                    label: 'Intercity NB KM',
                    data: sortedByNbKm.map((row) => INTER_NB[row.mp] || 0),
                    backgroundColor: palette.navy,
                    borderRadius: 4,
                  },
                  {
                    label: 'Intracity NB KM',
                    data: sortedByNbKm.map((row) => INTRA_NB[row.mp] || 0),
                    backgroundColor: palette.accent,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ${context.raw.toFixed(1)} KM`,
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    stacked: true,
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `${value} KM`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="card-surface p-6">
          <SectionTitle dot={palette.warning}>Deduction per 1,000 Span-KM</SectionTitle>
          <p className="dashboard-note">Normalised rupee intensity highlights structural leakage, not just absolute scale.</p>
          <div style={{ height: 280 }}>
            <Bar
              data={{
                labels: sortedByRate.map((row) => row.mp),
                datasets: [
                  {
                    label: '₹ deducted per 1,000 span-KM',
                    data: sortedByRate.map((row) => row.pen_per_km * 1000),
                    backgroundColor: palette.danger,
                    borderRadius: 4,
                  },
                ],
              }}
              options={{
                ...chartBaseOptions,
                plugins: {
                  tooltip: {
                    callbacks: {
                      label: (context) => ` ₹${Math.round(context.raw).toLocaleString('en-IN')} per 1,000 KM`,
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    border: { display: false },
                    ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                  },
                  y: {
                    grid: { color: palette.border },
                    border: { display: false },
                    ticks: {
                      callback: (value) => `₹${Math.round(value).toLocaleString('en-IN')}`,
                      color: palette.slateDeep,
                      font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        <div className="card-surface p-6">
          <SectionTitle dot={palette.danger}>2X Consecutive Failure Tracker</SectionTitle>
          <p className="dashboard-note">Only MPs with active repeat-failure surcharge are shown here.</p>
          <div style={{ height: 280 }}>
            {x2Rows.length ? (
              <Bar
                data={{
                  labels: x2Rows.map((row) => `${row.mp} (${row.circle})`),
                  datasets: [
                    {
                      label: '2X Repeat Penalty',
                      data: x2Rows.map((row) => row.x2_rs),
                      backgroundColor: palette.danger,
                      borderRadius: 4,
                    },
                  ],
                }}
                options={{
                  ...chartBaseOptions,
                  plugins: {
                    tooltip: {
                      callbacks: {
                        label: (context) => ` ${formatRupees(context.raw)}`,
                      },
                    },
                  },
                  scales: {
                    x: {
                      grid: { display: false },
                      border: { display: false },
                      ticks: { color: palette.slateDeep, font: { family: 'Inter', size: 10, weight: '500' } },
                    },
                    y: {
                      grid: { color: palette.border },
                      border: { display: false },
                      ticks: {
                        callback: (value) => `₹${Math.round(value).toLocaleString('en-IN')}`,
                        color: palette.slateDeep,
                        font: { family: 'JetBrains Mono', size: 10, weight: '500' },
                      },
                    },
                  },
                }}
              />
            ) : (
              <EmptyState title="No data for selected filters" className="h-full min-h-0" />
            )}
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between">
          <div>
            <SectionTitle dot={palette.navy}>MP-Level PM Performance Detail</SectionTitle>
            <p className="dashboard-note mb-0">Sortable operational view of billing loss, compliance, and repeat-failure exposure.</p>
          </div>
          <SearchField value={detailSearch} onChange={setDetailSearch} placeholder="Search MPs..." className="sm:max-w-xs" />
        </div>
        {detailRows.length ? (
          <div className="mt-4 table-scroll-shell">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th><SortButton label="Circle" sortKey="circle" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="MP" sortKey="mp" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="PM Base" sortKey="pm" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Billable" sortKey="bill" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Deduction" sortKey="ded" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="2X Ded." sortKey="x2_rs" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Billable %" sortKey="bill_pct" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="NB KM" sortKey="nb_km" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="₹ / 1000 KM" sortKey="pen_per_km" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Inter Miss" sortKey="inter_miss" sortState={detailSort} onSort={updateDetailSort} /></th>
                  <th><SortButton label="Intra Miss" sortKey="intra_miss" sortState={detailSort} onSort={updateDetailSort} /></th>
                </tr>
              </thead>
              <tbody>
                {detailRows.map((row) => (
                  <tr key={row.mp}>
                  <td>
                    <span
                      className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                      style={{ backgroundColor: `${CIRCLE_COLORS[row.circle]}16`, color: CIRCLE_COLORS[row.circle] }}
                    >
                      {row.circle}
                    </span>
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-700">{row.mp}</span>
                      {row.has_x2 ? <span className="status-badge status-badge-red">2X</span> : null}
                    </div>
                  </td>
                  <td className="mono-cell">{formatRupees(row.pm)}</td>
                  <td className="mono-cell">{formatRupees(row.bill)}</td>
                  <td className="mono-cell font-semibold" style={{ color: row.ded > 200000 ? palette.danger : row.ded > 100000 ? palette.warning : palette.navy }}>
                    {formatRupees(row.ded)}
                  </td>
                  <td className="mono-cell" style={{ color: row.x2_rs ? palette.danger : palette.slateDeep }}>
                    {row.x2_rs ? formatRupees(row.x2_rs) : '-'}
                  </td>
                  <td><ComplianceBar value={row.bill_pct} /></td>
                  <td className="mono-cell">{row.nb_km.toFixed(1)}</td>
                  <td className="mono-cell">{formatRupees(row.pen_per_km * 1000)}</td>
                  <td className={`mono-cell font-semibold ${missClass(row.inter_miss)}`}>{row.inter_miss.toFixed(1)}%</td>
                  <td className={`mono-cell font-semibold ${missClass(row.intra_miss)}`}>{row.intra_miss.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="mt-4 min-h-[180px]" />
        )}
      </div>

      <div className="card-surface p-6">
        <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div>
            <SectionTitle dot={palette.danger}>Span-Level PM Compliance Drilldown</SectionTitle>
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-500">
              <span>{`${spansInScope.length} spans in active scope`}</span>
              <span className="summary-chip bg-red-50 text-red-600">{`${totalSpanRiskCount} with 2X risk`}</span>
              <span className="summary-chip bg-amber-50 text-amber-700">{`${spanRows.length} spans shown`}</span>
              {spanTotalRisk > 0 ? (
                <span className="summary-chip bg-red-50 text-red-600">{`${formatRisk(spanTotalRisk)} at risk`}</span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:min-w-[520px] sm:flex-row">
            <SearchField value={spanSearch} onChange={setSpanSearch} placeholder="Search spans..." className="flex-1 sm:min-w-[220px]" />
            <FilterSelect label="MP" value={spanMpFilter} onChange={setSpanMpFilter} options={spanMpOptions} className="flex-1" />
            <FilterSelect label="Risk Filter" value={spanStatusFilter} onChange={setSpanStatusFilter} options={SPAN_STATUS_OPTIONS} className="flex-1" />
          </div>
        </div>

        {spanRows.length ? (
          <div className="table-scroll-shell-bordered">
            <table className="evidence-table">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>#</th>
                  <th>Span Code</th>
                  <th>MP</th>
                  <th>Circle</th>
                  <th>Span KM</th>
                  <th>NB KM at Risk</th>
                  <th>Nov Score</th>
                  <th>Dec Score</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {spanRows.map((span, index) => {
                  const status = spanStatus(span.dec, span.x2);
                  const novColor = scoreBarColor(span.nov);
                  const decColor = scoreBarColor(span.dec);
                  return (
                    <tr key={`${span.mp}-${span.span}`} className={span.x2 ? 'bg-red-50/40' : ''}>
                      <td className="mono-cell text-slate-400">{index + 1}</td>
                      <td className="max-w-[280px] whitespace-normal pr-6 font-medium text-slate-700">
                        {span.span}
                        {span.x2 ? <span className="ml-2 status-badge status-badge-red">2X</span> : null}
                      </td>
                      <td>{span.mp}</td>
                      <td>
                        <span
                          className="inline-flex rounded-full px-2 py-1 text-[10px] font-semibold"
                          style={{ backgroundColor: `${CIRCLE_COLORS[span.circle]}16`, color: CIRCLE_COLORS[span.circle] }}
                        >
                          {span.circle}
                        </span>
                      </td>
                      <td className="mono-cell">{span.km.toFixed(1)}</td>
                      <td className="mono-cell font-semibold" style={{ color: span.atRisk >= 50000 ? palette.danger : span.atRisk >= 10000 ? palette.warning : palette.accent }}>
                        {formatRisk(span.atRisk)}
                      </td>
                      <td>
                        <div className="flex min-w-[114px] items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(span.nov, 2)}%`, backgroundColor: novColor }} />
                          </div>
                          <span className="mono-cell text-[11px] font-semibold" style={{ color: novColor }}>{span.nov}%</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex min-w-[114px] items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-slate-100">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(span.dec, 2)}%`, backgroundColor: decColor }} />
                          </div>
                          <span className="mono-cell text-[11px] font-semibold" style={{ color: decColor }}>{span.dec}%</span>
                        </div>
                      </td>
                      <td><span className={status.className}>{status.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState icon="search" className="min-h-[220px]" />
        )}
      </div>

      <InsightBanner chips={PM_INSIGHT_CHIPS} />

      <div className="grid grid-cols-1 gap-4">
        {PM_INSIGHTS.map((insight) => (
          <InsightCard
            key={insight.title}
            tone={insight.tone}
            title={insight.title}
            exposure={insight.exposure}
            body={insight.body}
            actionTitle={insight.actionTitle}
            action={insight.action}
          />
        ))}
      </div>

      <div className="card-surface border border-emerald-200 bg-emerald-50/80 p-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-emerald-700">Recoverable Upside</div>
        <div className="mt-2 text-[13px] font-semibold leading-6 text-emerald-900">{PM_RECOVERABLE_BANNER}</div>
      </div>

      <div className="card-surface p-6">
        <SectionTitle dot={palette.success}>Prioritised Action Plan</SectionTitle>
        <div className="table-scroll-shell">
          <table className="action-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Action</th>
                <th>Impacted Scope</th>
                <th>Owner</th>
                <th>Impact</th>
              </tr>
            </thead>
            <tbody>
              {PM_ACTIONS.map((row) => (
                <tr key={row.action}>
                  <td><PriorityBadge priority={row.priority} /></td>
                  <td>
                    <div className="font-semibold text-slate-700">{row.action}</div>
                    <div className="mt-1 text-[11px] leading-5 text-slate-500">{row.detail}</div>
                  </td>
                  <td className="text-slate-600">{row.impacted}</td>
                  <td className="text-slate-600">{row.owner}</td>
                  <td className="text-[11px] leading-5 text-slate-600">{row.impact}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
